import { TeacherCourseCardList } from '@edu-frontend/modules/src/entities/user/';
import { H2, Spin, useGeneratePath, useStaticUrls } from 'platform-components';
import { getTeacherExams } from 'platform-components/src/components/app-factory/selectors';
import { UI_KIT_HEADER_TYPOGRAPHY } from 'platform-components/src/components/constants';
import { getMe } from 'platform-components/src/components/user/selectors';
import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useHistory, useParams } from 'react-router';
import { useMedia } from 'react-use';
import ym from 'react-yandex-metrika';

import { TeacherHeader } from 'common.components/TeacherHeader/TeacherHeader';
import { getCurrentOrFirstTeamId } from 'common.components/user/selectors';
import { actions } from 'common.components/widgets.course-container/actions';
import { isLinkCourseContainerToCourseLoading } from 'common.components/widgets.course-container/selectors';
import { actions as coursesActions } from 'store/courses/actions';
import { getCoursesByClass, getShouldLoadCourses } from 'store/courses/selectors';

import { MEDIA_QUERY_MOBILE } from '../library.calendar-plan/constants';

import './exams-training.scss';

export const TeacherExamsTraining = () => {
    const staticUrls = useStaticUrls();
    const dispatch = useDispatch();
    const generatePath = useGeneratePath();
    const history = useHistory();
    const params = useParams<Record<string, string>>();
    const isMobile = useMedia(MEDIA_QUERY_MOBILE);

    const me = useSelector(getMe);

    const teacherExams = useSelector(getTeacherExams);
    const teamId = useSelector((storeState) => getCurrentOrFirstTeamId(storeState, { classId: params.classId }));
    const shouldLoadCourses = useSelector((store) => getShouldLoadCourses(store, { classId: teamId }));
    const courses = useSelector((storeState) => getCoursesByClass(storeState, { classId: teamId }));
    const isLoading = useSelector(isLinkCourseContainerToCourseLoading);

    useEffect(() => {
        if (teamId && shouldLoadCourses) {
            dispatch(coursesActions.getCoursesByTeamRequest({ teamId }));
        }
    }, [dispatch, shouldLoadCourses, teamId]);

    const subject = 'informatics';
    const courseId = courses.find((course) => course.subject === subject && !course.date_closed)?.id;
    const slug = teacherExams?.OGE.mainCard.slug;

    const assignCourseContainer = useCallback(() => {
        if (!slug) {
            return;
        }

        if (!courseId || !teamId) {
            history.push(generatePath(staticUrls.labPreviewCourse, { subject, grade: '9', containerSlug: slug }));

            return;
        }

        const onSuccess = () => {
            history.push(
                generatePath(staticUrls.labLibrarySubjectCollection, {
                    subject,
                    containerSlug: slug,
                    classId: teamId,
                })
            );
        };

        dispatch(
            actions.postLinkCourseContainerToCourseRequest({
                courseContainerSlugs: [slug],
                courseId,
                onSuccess,
            })
        );
    }, [courseId, teamId, dispatch, slug, history, generatePath, staticUrls]);

    const handleSendEgeMetrika = () => {
        ym('reachGoal', 'teacher_button_click', { user_id: me?.id, from: 'exams-page-ege' });
    };

    const getLibraryThemeWithTeamIdUrl = useCallback(
        (url: string) => {
            let extraCardUrl = url;
            if (teamId) {
                const parsedExtraCardRoute = matchPath(extraCardUrl, staticUrls.deepLabTheme);

                if (parsedExtraCardRoute) {
                    extraCardUrl = generatePath(staticUrls.labLibrarySubjectTheme, {
                        classId: teamId,
                        ...parsedExtraCardRoute.params,
                    });
                }
            }

            return extraCardUrl;
        },
        [generatePath, staticUrls, teamId]
    );

    if (!teacherExams) {
        return null;
    }

    const imagePosition = isMobile ? 'center' : 'right';
    const extraCardUrl = getLibraryThemeWithTeamIdUrl(teacherExams.OGE.extraCard.url);

    const content = shouldLoadCourses ? (
        <Spin center />
    ) : (
        <>
            <H2 typography={UI_KIT_HEADER_TYPOGRAPHY.headlineLMedium}>{teacherExams.OGE.title}</H2>
            <TeacherCourseCardList
                imagePosition={imagePosition}
                items={[
                    {
                        ...teacherExams.OGE.mainCard,
                        onClick: assignCourseContainer,
                        isLoading,
                    },
                    {
                        ...teacherExams.OGE.extraCard,
                        url: extraCardUrl,
                    },
                ]}
            />
            {teacherExams.EGE.show && (
                <>
                    <H2 typography={UI_KIT_HEADER_TYPOGRAPHY.headlineLMedium}>{teacherExams.EGE.title}</H2>

                    <TeacherCourseCardList
                        imagePosition={imagePosition}
                        items={[{ ...teacherExams.EGE.card, onClick: handleSendEgeMetrika }]}
                    />
                </>
            )}
        </>
    );

    return (
        <>
            <Helmet title={teacherExams.title} />
            <TeacherHeader />
            <div className="teacher-exams-training">
                <div className="teacher-exams-training__wrapper">{content}</div>
            </div>
        </>
    );
};
